import { AsyncPipe, NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { PhoneInputComponent, TransactionAuthorizationComponent, UserMessagesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, IconComponent } from '@zero/shared/ui-web-design-system';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { TicketService } from '@zero/shared/util-services';
import { AuthService } from '@zero/shared/util-web-app-services';
import { BaseComponent } from '../base/base.component';
import { AppTanAdministrationComponent } from './app-tan-administration/app-tan-administration.component';

@Component({
  selector: 'self-service-security',
  templateUrl: './security.component.html',
  imports: [
    NgClass,
    AppTanAdministrationComponent,
    FormsModule,
    PhoneInputComponent,
    TransactionAuthorizationComponent,
    UserMessagesComponent,
    TranslocoPipe,
    AsyncPipe,
    ButtonComponent,
    IconComponent,
    NgbTooltipModule,
  ],
})
export class SecurityComponent extends BaseComponent {
  public newMobileNr?: string;
  public codeRequested = false;
  public verificationCode?: string;
  public state: 'init' | 'tanMobile' | 'inputNewMobile' | 'verifiyNewMobileNr' | 'inputNewEmail' = 'init';
  public dataToAuthorize?: AuthorizationData<AuthPayloadTicket>;
  public authenticationMode = false;

  #ticket?: Ticket;
  readonly #ticketService = inject(TicketService);
  readonly #authService = inject(AuthService);
  readonly #translateService = inject(TranslocoService);

  public appAuthEnabled$ = this.dataService.isAppAuthenticationEnabled();

  public changeMobile(): void {
    this.messageService.clearAllMessages();
    this.state = 'inputNewMobile';
  }

  public newMobile(mobileForm: NgForm): void {
    if (mobileForm.valid) {
      this.state = 'tanMobile';
    }
  }

  public cancelMobile(): void {
    this.codeRequested = false;
    this.state = 'init';
    this.dataToAuthorize = undefined;
    this.newMobileNr = undefined;
  }

  public startChangeMobileNr(): void {
    this.messageService.clearAllMessages();
    this.#ticket = new Ticket();
    this.#ticket.customerId = this.currentCustomer.custId;
    this.#ticket.type = 'MOBILENR';
    this.#ticket.ticketData = new TicketData();
    this.#ticket.ticketData.additionalProperties = {};
    this.#ticket.ticketData.additionalProperties[TicketData.ATTR_NEW_MOBILENR] = this.newMobileNr;
    this.dataToAuthorize = new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(this.#ticket));
    this.authenticationMode = true;
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    if (result.success) {
      this.messageService.clearAllMessages();
      this.#ticket = result.data;
      this.state = 'verifiyNewMobileNr';
      this.codeRequested = true;
      this.authenticationMode = false;
      this.verificationCode = undefined;
    } else if (result.reason === 'CanceledByUser') {
      this.state = 'init';
      this.dataToAuthorize = undefined;
      this.codeRequested = false;
      this.authenticationMode = false;
      this.verificationCode = undefined;
    }
  }

  public resendTanMobile(): void {
    this.messageService.clearAllMessages();
    this.#ticketService
      .requestTAN(this.#ticket)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.success && result.data) {
          this.#ticket = result.data;
          if (this.#ticket.status === 'PREPARED') {
            this.messageService.addInfoMessage(this.#translateService.translate('user-self-service.security.info-messages.PREPARED'), true);
          } else if (this.#ticket.status === 'OPEN') {
            this.codeRequested = true;
            this.messageService.addInfoMessage(this.#translateService.translate('user-self-service.security.info-messages.OPEN'), true);
            this.state = 'verifiyNewMobileNr';
          }
        }
        if (result.message) {
          this.messageService.addErrorMessage(result.message);
        }
      });
  }

  public confirmMobileCase(codeForm: NgForm): void {
    this.messageService.clearAllMessages();
    if (this.codeRequested && codeForm.valid) {
      this.#ticketService
        .verifyChangeMobileNr(this.#ticket, this.verificationCode)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) => {
            if (result.success && result.data) {
              this.messageService.clearAllMessages();
              this.messageService.addInfoMessage(
                this.#translateService.translate('user-self-service.security.info-messages.SUCCESS'),
                true,
              );
              this.codeRequested = false;
              this.verificationCode = undefined;
              this.state = 'init';
              this.#authService
                .refreshSession()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                  this.userService.loadContactData$(true);
                });
            }
            result.errorMessage && this.messageService.addErrorMessage(result.errorMessage);
          },
          error: (error) => this.messageService.addErrorMessage(error.message),
        });
    }
  }
}
